import { z } from 'zod';

// Shared presentation values only. No inspection service, Node API or token
// registry is imported; trusted callers supply the current design policy.
const Unit = z
  .string()
  .min(1)
  .max(160)
  .regex(/^[A-Za-z0-9_.:-]+$/u);
export const ColourToken = z
  .string()
  .min(1)
  .max(128)
  .regex(/^[^\u0000-\u001f\u007f]+$/u);
export const Spacing = z.number().finite().min(0).max(256);
const shape = z.strictObject({
  locale: z.string().min(2).max(48),
  style: z.enum(['decimal', 'percent', 'currency', 'unit']),
  minimumFractionDigits: z.int().min(0).max(20),
  maximumFractionDigits: z.int().min(0).max(20),
  useGrouping: z.boolean(),
  nullStyle: z.enum(['blank', 'dash', 'explicit']),
  currency: z
    .string()
    .regex(/^[A-Z]{3}$/u)
    .optional(),
  unit: Unit.optional(),
  scale: z.enum(['identity', 'fraction-to-percent']),
});
export const NumberFormat = z
  .unknown()
  .superRefine(checkPlainValue)
  .pipe(shape.superRefine(checkFormat));
export const PatchValue = z.union([z.string().max(10000), z.number().finite(), NumberFormat]);

export const normalizeNumberFormat = (value) => NumberFormat.parse(value);
export function normalizeColourToken(value, colours) {
  const key = ColourToken.parse(value);
  if (!Object.hasOwn(colours, key)) throw new TypeError('unknown_colour_token');
  return key;
}
export function normalizeSpacing(value, spacing) {
  if (typeof value === 'number') return Spacing.parse(value);
  if (typeof value !== 'string' || !Object.hasOwn(spacing, value))
    throw new TypeError('unknown_spacing_token');
  return Spacing.parse(spacing[value]);
}

function checkFormat(format, ctx) {
  const invalid = (message) => ctx.addIssue({ code: 'custom', message });
  if (format.minimumFractionDigits > format.maximumFractionDigits)
    invalid('invalid_fraction_digits');
  if ((format.style === 'percent') !== (format.scale === 'fraction-to-percent'))
    invalid('invalid_percent_scale');
  if ((format.style === 'currency') !== (format.currency !== undefined))
    invalid('invalid_currency_format');
  if ((format.style === 'unit') !== (format.unit !== undefined)) invalid('invalid_unit_format');
  try {
    new Intl.NumberFormat(format.locale, format);
  } catch {
    invalid('invalid_number_format');
  }
}

function checkPlainValue(value, ctx) {
  if (value === null || typeof value !== 'object') return;
  const prototype = Object.getPrototypeOf(value);
  const fields = Object.entries(Object.getOwnPropertyDescriptors(value));
  if (
    ![Object.prototype, null].includes(prototype) ||
    fields.some(
      ([key, field]) =>
        ['__proto__', 'prototype', 'constructor'].includes(key) ||
        field.get ||
        field.set ||
        !['string', 'number', 'boolean'].includes(typeof field.value),
    )
  )
    ctx.addIssue({ code: 'custom', message: 'invalid_presentation_value' });
}
